/**
 * Shared Linxup location → fleet list helpers (Control Center).
 */

export type FleetEntity = {
  key: string
  lat: number
  lng: number
  displayName: string
  assetTracker: boolean
  fleetId: number
  raw: Record<string, unknown>
}

export type FleetIssueCode = 'offline' | 'staleCheckIn' | 'lowBattery' | 'noRecentGps'

export type FleetIssue = {
  code: FleetIssueCode
  label: string
  detail?: string
}

export function asLocationArray(data: unknown): unknown[] {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  const obj = data as { data?: unknown; locations?: unknown }
  if (Array.isArray(obj.locations)) return obj.locations
  if (Array.isArray(obj.data)) return obj.data

  if (obj.data && typeof obj.data === 'object') {
    const nested = obj.data as { locations?: unknown }
    if (Array.isArray(nested.locations)) return nested.locations
  }
  return []
}

export function normalizeFleetEntities(data: unknown): FleetEntity[] {
  const list = asLocationArray(data)
  const out: FleetEntity[] = []
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue
    const o = raw as Record<string, unknown>
    const lat = Number(o.latitude ?? o.lat)
    const lng = Number(o.longitude ?? o.lng)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue
    const key = String(
      o.deviceUUID ||
        o.imei ||
        o.deviceSerialNumber ||
        o.serial ||
        o.serialNumber ||
        o.deviceId ||
        `${lat.toFixed(5)},${lng.toFixed(5)}`,
    )
    const first = String(o.firstName || '').trim()
    const last = String(o.lastName || '').trim()
    const personName = String(o.personName || '').trim()
    const displayLabel = String(o.name || o.deviceName || o.nickname || '').trim()
    const displayName =
      personName || [first, last].filter(Boolean).join(' ') || displayLabel || String(o.imei || 'Tracker')
    const assetTracker = Boolean(o.assetTracker)
    const fleetRaw = o.fleetId ?? o.groupId
    const fleetId =
      typeof fleetRaw === 'number' && Number.isFinite(fleetRaw)
        ? fleetRaw
        : Number(fleetRaw) || 0
    out.push({ key, lat, lng, displayName, assetTracker, fleetId, raw: o })
  }
  return out
}

export function compareDisplayNamesNatural(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
}

export function sortEntitiesByFleetThenName(entities: FleetEntity[]): FleetEntity[] {
  return [...entities].sort((a, b) => {
    if (a.fleetId !== b.fleetId) return a.fleetId - b.fleetId
    return compareDisplayNamesNatural(a.displayName, b.displayName)
  })
}

/** Fleet IDs marked hidden via group-labels API (`hiddenGroupIds`). */
export function buildHiddenFleetSet(hiddenGroupIds: string[] | undefined): Set<number> {
  const s = new Set<number>()
  if (!Array.isArray(hiddenGroupIds)) return s
  for (const id of hiddenGroupIds) {
    const n = Number(id)
    if (Number.isFinite(n)) s.add(n)
  }
  return s
}

export function groupEntitiesByFleet(entities: FleetEntity[]): { fleetId: number; items: FleetEntity[] }[] {
  const sorted = sortEntitiesByFleetThenName(entities)
  const map = new Map<number, FleetEntity[]>()
  for (const e of sorted) {
    const id = e.fleetId
    if (!map.has(id)) map.set(id, [])
    map.get(id)!.push(e)
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([fleetId, items]) => ({ fleetId, items }))
}

function parseEpochMs(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Some payloads may send seconds; normalize to milliseconds.
    return value < 10_000_000_000 ? value * 1000 : value
  }
  if (typeof value === 'string' && value.trim()) {
    const raw = value.trim()
    const n = Number(raw)
    if (Number.isFinite(n)) return n < 10_000_000_000 ? n * 1000 : n
    const parsed = Date.parse(raw)
    if (Number.isFinite(parsed)) return parsed

    // Common API variant: "YYYY-MM-DD HH:mm:ss" (without timezone).
    const isoLike = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/,
    )
    if (isoLike) {
      const [, y, m, d, hh, mm, ss] = isoLike
      const ms = Date.UTC(
        Number(y),
        Number(m) - 1,
        Number(d),
        Number(hh),
        Number(mm),
        Number(ss || '0'),
      )
      if (Number.isFinite(ms)) return ms
    }

    // Common API variant: "MM/DD/YYYY HH:mm:ss".
    const usLike = raw.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/,
    )
    if (usLike) {
      const [, m, d, y, hh, mm, ss] = usLike
      const ms = Date.UTC(
        Number(y),
        Number(m) - 1,
        Number(d),
        Number(hh),
        Number(mm),
        Number(ss || '0'),
      )
      if (Number.isFinite(ms)) return ms
    }
  }
  return null
}

function extractBatteryPercent(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.min(100, value))
  }
  const raw = String(value ?? '').trim()
  if (!raw) return null
  const pctMatch = raw.match(/(-?\d+(?:\.\d+)?)\s*%/)
  if (pctMatch) {
    const pct = Number(pctMatch[1])
    if (Number.isFinite(pct)) return Math.max(0, Math.min(100, pct))
  }
  return null
}

export function getEntityLastGpsTimestampMs(entity: FleetEntity): number | null {
  const candidates = [
    entity.raw.date,
    entity.raw.editDate,
    entity.raw.gpsDate,
    entity.raw.deviceDate,
    entity.raw.reportDate,
    entity.raw.lastReportDate,
    entity.raw.lastGpsDate,
    entity.raw.lastUpdate,
    entity.raw.lastUpdated,
    entity.raw.lastLocationDate,
    entity.raw.updatedAt,
  ]
  for (const c of candidates) {
    const parsed = parseEpochMs(c)
    if (parsed != null) return parsed
  }
  return null
}

export function getEntityDriverName(entity: FleetEntity): string | null {
  const candidates = [
    entity.raw.appDriverName,
    entity.raw.driverName,
    entity.raw.driver,
    entity.raw.currentDriverName,
  ]
  for (const c of candidates) {
    const name = String(c ?? '').trim()
    if (name) return name
  }
  return null
}

export function deriveFleetIssues(entity: FleetEntity, nowMs = Date.now()): FleetIssue[] {
  const issues: FleetIssue[] = []
  const status = String(entity.raw.status || '').trim().toLowerCase()
  const lastGpsMs = getEntityLastGpsTimestampMs(entity)
  const staleThresholdMs = 24 * 60 * 60 * 1000

  if (
    status.includes('offline') ||
    status.includes('disconnected') ||
    status.includes('no signal') ||
    status.includes('not reporting')
  ) {
    issues.push({ code: 'offline', label: 'Offline' })
  }

  if (!lastGpsMs) {
    issues.push({ code: 'noRecentGps', label: 'No recent GPS check-in' })
  } else {
    const ageMs = Math.max(0, nowMs - lastGpsMs)
    if (ageMs > staleThresholdMs) {
      const hours = Math.round(ageMs / (60 * 60 * 1000))
      issues.push({
        code: 'staleCheckIn',
        label: 'Stale check-in',
        detail: `${hours}h since last update`,
      })
    }
  }

  const batteryPct = extractBatteryPercent(entity.raw.battery)
  if (batteryPct != null && batteryPct <= 20) {
    issues.push({
      code: 'lowBattery',
      label: 'Low battery',
      detail: `${Math.round(batteryPct)}%`,
    })
  }

  return issues
}
